/**
 * Statusmeldung (G-10): Planzeile + Grund → Abwesenheits-Upsert am Planzeilen-Tag.
 */

import { useEffect, useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import { ABSENCE_REASONS, ABSENCE_REASON_VALUES } from "@/lib/absenceColors"
import { fmtDate } from "@/lib/format"
import { api, apiError } from "@/services/api"
import type { AbwesenheitGrund, ShiftPlanEntry } from "@/types"
import { PlanEntryPicker } from "./PlanEntryPicker"

interface ReportAbsenceDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Stichtag des Boards ("YYYY-MM-DD") */
  date: string
}

export function ReportAbsenceDialog({ open, onOpenChange, date }: ReportAbsenceDialogProps) {
  const queryClient = useQueryClient()
  const [entry, setEntry] = useState<ShiftPlanEntry | null>(null)
  const [reason, setReason] = useState<AbwesenheitGrund>("KRANK")

  useEffect(() => {
    if (open) {
      setEntry(null)
      setReason("KRANK")
    }
  }, [open])

  const report = useMutation({
    mutationFn: () =>
      api.attendance.reportAbsence({ shiftPlanEntryId: Number(entry!.id), reason }),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ["attendance"] })
      queryClient.invalidateQueries({ queryKey: ["shiftPlan"] })
      queryClient.invalidateQueries({ queryKey: ["absences"] })
      toast.success(
        `${entry!.employeeName}: ${ABSENCE_REASONS[r.reason].label} gemeldet (${fmtDate(r.from)} – ${fmtDate(r.to)})`
      )
      onOpenChange(false)
    },
    onError: (e) => toast.error(apiError(e)),
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Abwesenheit melden</DialogTitle>
          <DialogDescription>
            Erzeugt bzw. aktualisiert die Abwesenheit am Planzeilen-Tag (G-10).
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <PlanEntryPicker date={date} value={entry} onChange={setEntry} />

          <div className="space-y-1.5">
            <Label>Grund *</Label>
            <Select
              value={reason}
              onChange={(e) => setReason(e.target.value as AbwesenheitGrund)}
            >
              {ABSENCE_REASON_VALUES.map((r) => (
                <option key={r} value={r}>
                  {ABSENCE_REASONS[r].label}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button disabled={!entry || report.isPending} onClick={() => report.mutate()}>
            Melden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
